import type { ReactNode } from "react";
import { Container, MarketingPage, MButton, Eyebrow } from "./ui";
import { SITE, breadcrumb } from "./seo";

/* ─── Shared document primitives ──────────────────────────────────────────── */
const UPDATED = "June 11, 2026";

function LegalLayout({
  title,
  updated,
  children,
}: {
  title: string;
  updated: string;
  children: ReactNode;
}) {
  return (
    <section className="bg-warm-bg font-sans">
      <Container className="pt-16 pb-24 sm:pt-20">
        <div className="mx-auto max-w-3xl">
          <header className="border-b border-warm-border pb-8">
            <Eyebrow>Legal</Eyebrow>
            <h1 className="mt-6 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              {title}
            </h1>
            <p className="mt-4 text-warm-muted">Last updated: {updated}</p>
          </header>
          <div className="mt-8">{children}</div>
        </div>
      </Container>
    </section>
  );
}

function H2({ children }: { children: ReactNode }) {
  return <h2 className="mt-10 mb-3 text-xl font-semibold text-warm-ink">{children}</h2>;
}

function P({ children }: { children: ReactNode }) {
  return <p className="mb-4 text-[15px] leading-relaxed text-warm-muted">{children}</p>;
}

function UL({ children }: { children: ReactNode }) {
  return <ul className="mb-4 list-disc space-y-2 pl-5">{children}</ul>;
}

function LI({ children }: { children: ReactNode }) {
  return <li className="text-[15px] leading-relaxed text-warm-muted">{children}</li>;
}

function MailLink({ address }: { address: string }) {
  return (
    <a href={`mailto:${address}`} className="text-warm-accent hover:underline">
      {address}
    </a>
  );
}

function InlineLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <a href={to} className="text-warm-accent hover:underline">
      {children}
    </a>
  );
}

/* ─── Privacy Policy ──────────────────────────────────────────────────────── */
export function Privacy() {
  return (
    <MarketingPage
      seo={{
        title: "Privacy Policy — WonderfulInterview",
        description:
          "How WonderfulInterview collects, uses, and protects employer and candidate data across AI video interviews, evaluations, recordings, and billing.",
        path: "/privacy",
        jsonLd: breadcrumb([
          { name: "Home", path: "/" },
          { name: "Privacy", path: "/privacy" },
        ]),
      }}
    >
      <LegalLayout title="Privacy Policy" updated={UPDATED}>
        <P>
          This Privacy Policy explains how {SITE.name}, a Wonderful Agent product available at{" "}
          <InlineLink to={SITE.url}>{SITE.url}</InlineLink>, collects, uses, and protects
          information when you use our platform. It is provided for transparency and may be
          updated from time to time; for contractual data-processing terms, please contact us.
        </P>

        <H2>1. Overview &amp; scope</H2>
        <P>
          {SITE.name} is a pay-as-you-go AI interview platform. Employers create AI video
          interviews, candidates self-schedule by email and complete a recorded AI video
          interview, and the platform returns an AI-written evaluation. This policy covers both
          the employers and teams who use the platform and the candidates who are interviewed
          through it.
        </P>
        <P>
          For most candidate data, the employer is the data controller and {SITE.name} acts as a
          processor on their behalf. We act as a controller for the account, billing, and
          security data we collect to operate the service.
        </P>

        <H2>2. Information we collect</H2>
        <UL>
          <LI>
            <strong className="text-warm-ink">Account &amp; company data.</strong> Names, work
            email addresses, company details, team roles, and authentication information for the
            people who administer an account.
          </LI>
          <LI>
            <strong className="text-warm-ink">Candidate data.</strong> Candidate name and email,
            scheduling messages, interview transcripts, and audio/video recordings of the
            interview session, along with the AI-generated evaluation derived from them.
          </LI>
          <LI>
            <strong className="text-warm-ink">Usage &amp; billing data.</strong> Product usage,
            log and diagnostic data, the number of completed interviews, and the payment
            information needed to bill for paid interviews (processed by our payments provider).
          </LI>
          <LI>
            <strong className="text-warm-ink">Cookies &amp; local storage.</strong> We use only
            essential cookies and authentication tokens required to keep you signed in and to
            operate the service. We do not use third-party advertising or cross-site tracking.
          </LI>
        </UL>

        <H2>3. How we use information</H2>
        <P>We use the information we collect to:</P>
        <UL>
          <LI>Provide, maintain, and improve the interview platform.</LI>
          <LI>Schedule interviews and send the related transactional emails.</LI>
          <LI>Generate AI evaluations and integrity signals from interview sessions.</LI>
          <LI>Bill for completed interviews and process payments.</LI>
          <LI>Protect the security and integrity of the service and prevent abuse.</LI>
          <LI>Provide customer support and respond to requests.</LI>
          <LI>Comply with applicable legal obligations.</LI>
        </UL>

        <H2>4. AI processing &amp; automated evaluation</H2>
        <P>
          Interview transcripts are analyzed by AI models to produce a written evaluation. Where
          the session indicates possible integrity concerns, those signals are surfaced as
          evidence alongside the relevant context so a person can review them.
        </P>
        <P>
          Evaluations are decision-support tools. We do not make solely-automated decisions about
          candidates that the employer cannot review; the employer retains responsibility for
          hiring decisions and can review the evidence behind any evaluation.
        </P>

        <H2>5. Candidate recordings &amp; consent</H2>
        <P>
          Interview recordings are captured with the candidate&apos;s consent at the start of the
          session. Recordings are stored privately and served only through short-lived signed
          links. They are excluded from any publicly shared report. Employers control the
          retention of recordings associated with their account.
        </P>

        <H2>6. Sub-processors</H2>
        <P>We rely on a small set of trusted providers to operate the service:</P>
        <UL>
          <LI>
            <strong className="text-warm-ink">Tavus</strong> — AI video interview provider that
            conducts and records the interview.
          </LI>
          <LI>
            <strong className="text-warm-ink">RouterBench</strong> — AI model gateway used to run
            Claude models for evaluation and scheduling.
          </LI>
          <LI>
            <strong className="text-warm-ink">Resend</strong> — transactional email delivery.
          </LI>
          <LI>
            <strong className="text-warm-ink">Stripe</strong> — payment processing.
          </LI>
          <LI>
            <strong className="text-warm-ink">Cloudflare</strong> — cloud hosting and storage
            (Workers, Pages, and R2).
          </LI>
          <LI>
            <strong className="text-warm-ink">Neon</strong> — managed Postgres database.
          </LI>
        </UL>
        <P>
          Data may be processed in the regions in which these providers operate. Each provider is
          bound by its own data-protection commitments.
        </P>

        <H2>7. Data sharing</H2>
        <P>
          We do not sell personal data. We share information only with the sub-processors listed
          above to deliver the service, and where required to comply with the law or to protect
          our rights, users, or the public.
        </P>

        <H2>8. Data retention</H2>
        <P>
          We retain information for as long as needed to provide the service and to meet our
          legal obligations. Employers may request deletion of candidate data associated with
          their account, and we will action such requests subject to any legal retention
          requirements.
        </P>

        <H2>9. Security</H2>
        <P>
          We use encryption in transit and at rest for sensitive secrets, role-based access
          controls, and signed webhooks to protect data integrity. You can read more on our{" "}
          <InlineLink to="/security">security page</InlineLink>. Security concerns can be reported
          to <MailLink address="security@wonderfulagent.ai" />.
        </P>

        <H2>10. Your rights</H2>
        <P>
          Depending on your location, you may have rights to access, correct, delete, export, or
          object to the processing of your personal data (for example under GDPR or CCPA). To
          exercise these rights, contact us at <MailLink address="privacy@wonderfulagent.ai" />.
          Candidates may also contact the employer who invited them, as the controller, or reach
          out to us and we will help route the request.
        </P>

        <H2>11. International transfers</H2>
        <P>
          Where data is transferred across borders, we and our providers use appropriate
          safeguards, such as standard contractual clauses, to protect it in line with
          applicable law.
        </P>

        <H2>12. Children</H2>
        <P>
          The service is intended for use in professional hiring and is not directed to children
          under 16. We do not knowingly collect personal data from anyone under that age.
        </P>

        <H2>13. Changes to this policy</H2>
        <P>
          We may update this policy as the service evolves or as legal requirements change. When
          we make material changes, we will update the &ldquo;Last updated&rdquo; date above and,
          where appropriate, notify account administrators.
        </P>

        <H2>14. Contact</H2>
        <P>
          Questions about this policy or your data can be sent to{" "}
          <MailLink address="privacy@wonderfulagent.ai" />.
        </P>

        <div className="mt-10 border-t border-warm-border pt-8">
          <MButton to="/terms" variant="outline">
            Read our Terms of Service
          </MButton>
        </div>
      </LegalLayout>
    </MarketingPage>
  );
}

/* ─── Terms of Service ────────────────────────────────────────────────────── */
export function Terms() {
  return (
    <MarketingPage
      seo={{
        title: "Terms of Service — WonderfulInterview",
        description:
          "The terms that govern use of WonderfulInterview, a pay-as-you-go AI interview platform: accounts, acceptable use, billing, candidate consent, and liability.",
        path: "/terms",
        jsonLd: breadcrumb([
          { name: "Home", path: "/" },
          { name: "Terms", path: "/terms" },
        ]),
      }}
    >
      <LegalLayout title="Terms of Service" updated={UPDATED}>
        <H2>1. Agreement to terms</H2>
        <P>
          These Terms of Service govern your access to and use of {SITE.name}, a Wonderful Agent
          product available at <InlineLink to={SITE.url}>{SITE.url}</InlineLink>. By creating an
          account or using the service, you agree to these terms on behalf of yourself and any
          organization you represent. If you do not agree, do not use the service.
        </P>

        <H2>2. The service</H2>
        <P>
          {SITE.name} is an AI interview platform. Employers create AI video interviews,
          candidates self-schedule by email and complete a recorded AI video interview, and the
          platform returns an AI-written evaluation with supporting evidence to assist hiring
          teams.
        </P>

        <H2>3. Accounts &amp; teams</H2>
        <UL>
          <LI>You must provide accurate account information and keep it up to date.</LI>
          <LI>
            Access is role-based; you are responsible for the actions of teammates you invite and
            for the roles you assign them.
          </LI>
          <LI>
            You are responsible for keeping your credentials secure and for all activity under
            your account.
          </LI>
        </UL>

        <H2>4. Acceptable use</H2>
        <P>You agree not to use the service to:</P>
        <UL>
          <LI>Violate any law or engage in unlawful or discriminatory hiring practices.</LI>
          <LI>Process data in breach of applicable employment or privacy laws.</LI>
          <LI>Misuse the AI, including attempts to manipulate or distort evaluations.</LI>
          <LI>
            Probe, scan, or breach the security of the service, or scrape or extract data without
            authorization.
          </LI>
        </UL>

        <H2>5. Candidate consent &amp; fair use</H2>
        <P>
          As the employer, you are responsible for obtaining each candidate&apos;s consent to be
          recorded and evaluated, for using the service only for lawful and non-discriminatory
          hiring purposes, and for complying with the interview, recording, and AI-in-hiring laws
          that apply in your jurisdiction.
        </P>
        <P>
          AI evaluations are decision-support and are not a substitute for human judgment. You
          must not base final hiring decisions solely on automated output where the law requires
          human review.
        </P>

        <H2>6. Billing</H2>
        <UL>
          <LI>The service is pay-as-you-go and billed per completed-and-evaluated interview.</LI>
          <LI>
            The price is quoted and locked at the time you send an interview invite, so you always
            know the cost in advance.
          </LI>
          <LI>A valid payment method is required to send paid interviews.</LI>
          <LI>No-shows and incomplete sessions that are not evaluated are not billed.</LI>
          <LI>Prices are exclusive of taxes, which may be added where required.</LI>
          <LI>Failed or overdue payments may result in suspension of the service.</LI>
        </UL>

        <H2>7. Intellectual property</H2>
        <P>
          We own the platform, including its software, design, and underlying technology. You
          retain ownership of your content and data, including candidate data you process through
          the service. We grant you a limited, non-exclusive, non-transferable license to use the
          platform during your subscription, and you grant us the limited license needed to
          operate the service on your behalf.
        </P>

        <H2>8. Third-party services</H2>
        <P>
          The service relies on third-party sub-processors for video interviews, AI models,
          email, payments, hosting, and database services. Their availability and performance are
          outside our direct control, and your use of the service is also subject to their
          applicable terms.
        </P>

        <H2>9. Disclaimers</H2>
        <P>
          The service is provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo; without
          warranties of any kind, whether express or implied. AI-generated output may contain
          errors or omissions, and we do not warrant that evaluations are accurate, complete, or
          error-free. You are responsible for reviewing output before acting on it.
        </P>

        <H2>10. Limitation of liability</H2>
        <P>
          To the maximum extent permitted by law, neither party will be liable for indirect,
          incidental, special, consequential, or punitive damages. Our total liability arising out
          of or relating to the service will not exceed the amounts you paid to us for the service
          in the twelve months preceding the event giving rise to the claim.
        </P>

        <H2>11. Indemnification</H2>
        <P>
          You agree to indemnify and hold harmless {SITE.name} and its affiliates from claims,
          damages, and expenses arising out of your use of the service, your content, or your
          breach of these terms or of applicable law, including employment and privacy laws.
        </P>

        <H2>12. Termination</H2>
        <P>
          Either party may terminate this agreement at any time. We may suspend or terminate
          access if you breach these terms or use the service in a way that creates risk or
          liability. On termination, your right to use the service ends; provisions that by their
          nature should survive — such as billing for prior use, disclaimers, and limitations of
          liability — will survive.
        </P>

        <H2>13. Changes to the service &amp; terms</H2>
        <P>
          We may modify the service and these terms over time. When we make material changes to
          these terms, we will update the &ldquo;Last updated&rdquo; date above. Your continued use
          of the service after changes take effect constitutes acceptance of the updated terms.
        </P>

        <H2>14. Governing law</H2>
        <P>
          These terms are governed by the laws of the jurisdiction in which {SITE.name} operates,
          without regard to its conflict-of-laws rules.
        </P>

        <H2>15. Contact</H2>
        <P>
          Questions about these terms can be sent to <MailLink address="legal@wonderfulagent.ai" />.
        </P>

        <div className="mt-10 border-t border-warm-border pt-8">
          <MButton to="/privacy" variant="outline">
            Read our Privacy Policy
          </MButton>
        </div>
      </LegalLayout>
    </MarketingPage>
  );
}
